// Hosts whose embedded iframes set tracking cookies on load and so must be consent-gated. These are iframe `src` hosts only (e.g. FB social plugins load from facebook.com); SDK script hosts like connect.facebook.net are gated elsewhere, not here.
const TRACKING_EMBED_HOSTS = [
  'youtube.com',
  'youtube-nocookie.com',
  'facebook.com',
  'tiktok.com',
  'vimeo.com',
] as const;

// The dummy base only exists so the parser accepts protocol-relative and relative srcs; relative ones resolve to that host and so never match the blacklist.
const getEmbedHost = (src: string): string => {
  try {
    return new URL(src, 'https://embed.invalid').hostname.toLowerCase();
  } catch {
    return '';
  }
};

const hostMatches = (host: string, base: string): boolean =>
  host === base || host.endsWith(`.${base}`);

export const isTrackingEmbed = (src: string | undefined): boolean => {
  if (!src) {
    return false;
  }

  const host = getEmbedHost(src);
  if (!host) {
    return false;
  }

  return TRACKING_EMBED_HOSTS.some((base) => hostMatches(host, base));
};

// Forces any YouTube embed onto the canonical no-cookie host. That domain only serves www.youtube-nocookie.com, so the whole host is replaced rather than preserving the original subdomain (e.g. m.youtube.com, or a bare youtube-nocookie.com). No-op for everything else.
export const toPrivacyEnhancedSrc = (src: string): string => {
  const host = getEmbedHost(src);

  if (hostMatches(host, 'youtube.com') || hostMatches(host, 'youtube-nocookie.com')) {
    return src.replace(/(\/\/)[^/]*(\/|$)/, '$1www.youtube-nocookie.com$2');
  }

  return src;
};

export const getEmbedProviderLabel = (src: string | undefined): string | null => {
  if (!src) {
    return null;
  }

  const host = getEmbedHost(src);

  if (hostMatches(host, 'youtube.com') || hostMatches(host, 'youtube-nocookie.com')) {
    return 'YouTube';
  }
  if (hostMatches(host, 'facebook.com')) {
    return 'Facebook';
  }
  if (hostMatches(host, 'tiktok.com')) {
    return 'TikTok';
  }
  if (hostMatches(host, 'vimeo.com')) {
    return 'Vimeo';
  }

  return null;
};
